import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Button,
  CircularProgress,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import EmailIcon from '@mui/icons-material/Email';
import SmsIcon from '@mui/icons-material/Sms';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import { ConfirmDialog } from '@/components/common/ConfirmDialog';
import { ResponsiveDialog } from '@/components/common/ResponsiveDialog';
import { useDeliverOffer, type DeliverOfferBody, type WorkOrderOffer } from '@/hooks/useWorkOrderOffers';
import {
  useClearNoWhatsApp,
  useRecordNoWhatsApp,
  useSmsRecipients,
  useSmsStatus,
} from '@/features/settings/sms/useSmsIntegration';
import { useWhatsAppStatus } from '@/hooks/useWhatsAppSettings';
import { useNotify } from '@/stores/notificationStore';
import { describeApiError } from '@/lib/describeApiError';
import { toDateTimeLocalValue } from './offerDisplay';

type OfferChannel = DeliverOfferBody['channel'];

interface SendOfferDialogProps {
  open: boolean;
  workOrderId: string;
  offer: WorkOrderOffer;
  contactEmail: string | null;
  contactPhone: string | null;
  resend: boolean;
  onClose: () => void;
}

export function SendOfferDialog({
  open,
  workOrderId,
  offer,
  contactEmail,
  contactPhone,
  resend,
  onClose,
}: SendOfferDialogProps) {
  const { t } = useTranslation('workorders');
  const { t: tc } = useTranslation('common');
  const notify = useNotify();
  const deliver = useDeliverOffer();
  const whatsappStatus = useWhatsAppStatus(open);
  const whatsappEnabled = whatsappStatus.data?.enabled ?? false;
  const smsStatus = useSmsStatus(open);
  const smsEnabled = smsStatus.data?.enabled ?? false;
  const smsRepliesEnabled = smsStatus.data?.repliesEnabled ?? false;

  const [channel, setChannel] = useState<OfferChannel>('email');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [message, setMessage] = useState('');
  const [expiresAt, setExpiresAt] = useState('');
  const [submitError, setSubmitError] = useState<string | null>(null);
  // Recording "does not use WhatsApp" changes how every later message reaches the customer, so it is confirmed first.
  const [confirmRecordOpen, setConfirmRecordOpen] = useState(false);

  // WhatsApp is primary: a text message goes only to a customer a person has recorded as not using it.
  const smsRecipients = useSmsRecipients(workOrderId, open && channel === 'sms');
  const recordNoWhatsApp = useRecordNoWhatsApp(workOrderId);
  const clearNoWhatsApp = useClearNoWhatsApp(workOrderId);
  const orderer = smsRecipients.data?.recipients.find((r) => r.roles.includes('orderer')) ?? null;
  const smsAllowed = channel !== 'sms' || (orderer !== null && orderer.whatsapp === 'confirmed_absent' && !orderer.optedOut);

  useEffect(() => {
    if (!open) return;
    setChannel(
      contactEmail
        ? 'email'
        : contactPhone && whatsappEnabled
          ? 'whatsapp'
          : contactPhone && smsEnabled
            ? 'sms'
            : 'email',
    );
    setEmail(contactEmail ?? '');
    setPhone(contactPhone ?? '');
    setMessage('');
    setExpiresAt(offer.expiresAt ? toDateTimeLocalValue(new Date(offer.expiresAt)) : '');
    setSubmitError(null);
    setConfirmRecordOpen(false);
  }, [open, contactEmail, contactPhone, whatsappEnabled, smsEnabled, offer.expiresAt]);

  useEffect(() => {
    if (channel === 'whatsapp' && !whatsappEnabled) setChannel('email');
    if (channel === 'sms' && !smsEnabled) setChannel('email');
  }, [channel, whatsappEnabled, smsEnabled]);

  const recipient = channel === 'email' ? email.trim() : phone.trim();
  // An SMS needs no typed recipient: the server texts the number recorded with the confirmation.
  const canSend = (channel === 'sms' || recipient !== '') && !deliver.isPending && smsAllowed;

  const handleRecipientAction = async (action: 'record' | 'withdraw') => {
    if (orderer === null) return;
    const target = { partyType: orderer.partyType, partyId: orderer.partyId };
    try {
      if (action === 'record') {
        await recordNoWhatsApp.mutateAsync(target);
        notify.success(t('offers.noWhatsapp.recorded'), t('offers.noWhatsapp.recordedDesc', { name: orderer.name }));
      } else {
        await clearNoWhatsApp.mutateAsync(target);
        notify.success(t('offers.noWhatsapp.withdrawn'), t('offers.noWhatsapp.withdrawnDesc', { name: orderer.name }));
      }
    } catch (error) {
      const reason = describeApiError(error, t, {
        codePrefix: 'offers.errorCode',
        fallback: t(action === 'record' ? 'offers.noWhatsapp.recordFailed' : 'offers.noWhatsapp.withdrawFailed'),
      });
      notify.error(t(action === 'record' ? 'offers.noWhatsapp.recordFailed' : 'offers.noWhatsapp.withdrawFailed'), reason);
    }
  };

  const handleConfirm = async () => {
    if (!canSend) return;
    setSubmitError(null);
    // A text message goes to the number the customer was confirmed not to use WhatsApp on: the server
    // takes it from the record, so no number is sent (a typed one would only be refused).
    const body: DeliverOfferBody = channel === 'sms' ? { channel } : { channel, recipient };
    const trimmedMessage = message.trim();
    if (trimmedMessage) body.message = trimmedMessage;
    if (expiresAt) {
      const parsed = new Date(expiresAt);
      if (!Number.isNaN(parsed.getTime())) body.expiresAt = parsed.toISOString();
    }
    try {
      const result = await deliver.mutateAsync({
        workOrderId,
        offerId: offer.id,
        data: body,
      });
      if (!result.delivered && result.reason === 'preapproved') {
        notify.info(t('offers.preapprovedTitle'), t('offers.preapprovedDesc'));
      } else {
        notify.success(t('offers.sentTitle'), t('offers.sentDesc'));
      }
      onClose();
    } catch (error) {
      const messageText = describeApiError(error, t, {
        codePrefix: 'offers.errorCode',
        fallback: t('offers.sendFailed'),
      });
      setSubmitError(messageText);
      notify.error(t('offers.sendFailed'), messageText);
    }
  };

  return (
    <ResponsiveDialog
      open={open}
      onClose={deliver.isPending ? undefined : onClose}
      mobileTitle={resend ? t('offers.resendTitle') : t('offers.sendTitle')}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>{resend ? t('offers.resendTitle') : t('offers.sendTitle')}</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
        {submitError && <Alert severity="error">{submitError}</Alert>}
        <ToggleButtonGroup
          value={channel}
          exclusive
          onChange={(_event, value: OfferChannel | null) => {
            if (value) setChannel(value);
          }}
          size="small"
          aria-label={t('offers.deliverChannelLabel')}
        >
          <ToggleButton value="email">
            <EmailIcon fontSize="small" sx={{ mr: 1 }} />
            {t('offers.deliverChannel.email')}
          </ToggleButton>
          <ToggleButton value="whatsapp" disabled={!whatsappEnabled}>
            <WhatsAppIcon fontSize="small" sx={{ mr: 1 }} />
            {t('offers.deliverChannel.whatsapp')}
          </ToggleButton>
          <ToggleButton value="sms" disabled={!smsEnabled}>
            <SmsIcon fontSize="small" sx={{ mr: 1 }} />
            {t('offers.deliverChannel.sms')}
          </ToggleButton>
        </ToggleButtonGroup>
        {!whatsappEnabled && !whatsappStatus.isPending && (
          <Typography variant="caption" color="text.secondary">
            {t('offers.whatsappUnavailable')}
          </Typography>
        )}
        {!smsEnabled && !smsStatus.isPending && (
          <Typography variant="caption" color="text.secondary">
            {t('offers.smsUnavailable')}
          </Typography>
        )}
        {/* WhatsApp is the primary channel: SMS is only for a customer who does not use it, or follows a failed delivery. */}
        {channel === 'whatsapp' && smsEnabled && (
          <Typography variant="caption" color="text.secondary">
            {t('offers.whatsappFallbackHint')}
          </Typography>
        )}
        {channel === 'sms' && (
          <Typography variant="caption" color="text.secondary">
            {smsRepliesEnabled ? t('offers.smsOnlyFallbackReplyHint') : t('offers.smsOnlyFallbackHint')}
          </Typography>
        )}
        {channel === 'sms' &&
          (smsRecipients.isPending ? (
            <CircularProgress size={20} aria-label={tc('loading')} />
          ) : smsRecipients.isError ? (
            <Alert severity="error">{t('offers.noWhatsapp.loadFailed')}</Alert>
          ) : orderer === null ? (
            <Alert severity="warning">{t('offers.noWhatsapp.noRecipient')}</Alert>
          ) : orderer.optedOut ? (
            <Alert severity="error">{t('offers.noWhatsapp.optedOut', { name: orderer.name })}</Alert>
          ) : orderer.whatsapp === 'confirmed_absent' ? (
            <Alert
              severity="success"
              action={
                <Button
                  color="inherit"
                  size="small"
                  disabled={clearNoWhatsApp.isPending}
                  onClick={() => {
                    void handleRecipientAction('withdraw');
                  }}
                >
                  {t('offers.noWhatsapp.withdraw')}
                </Button>
              }
            >
              {t('offers.noWhatsapp.confirmed', { name: orderer.name, number: orderer.smsDestinationMasked ?? '' })}
            </Alert>
          ) : (
            <Alert
              severity="warning"
              action={
                <Button
                  color="inherit"
                  size="small"
                  disabled={recordNoWhatsApp.isPending || orderer.smsDestinationMasked === null}
                  onClick={() => {
                    setConfirmRecordOpen(true);
                  }}
                >
                  {t('offers.noWhatsapp.record')}
                </Button>
              }
            >
              {orderer.smsDestinationMasked === null
                ? t('offers.noWhatsapp.noNumber', { name: orderer.name })
                : orderer.whatsapp === 'stale'
                  ? t('offers.noWhatsapp.stale', { name: orderer.name })
                  : t('offers.noWhatsapp.needed', { name: orderer.name })}
            </Alert>
          ))}

        {channel === 'email' ? (
          <TextField
            label={t('offers.recipientEmail')}
            type="email"
            value={email}
            onChange={(event) => {
              setEmail(event.target.value);
            }}
            size="small"
            fullWidth
            required
          />
        ) : channel === 'sms' ? (
          // The number the server will text is the confirmed one shown above, never the contact phone.
          <Typography variant="caption" color="text.secondary">
            {t('offers.smsRecipientLocked')}
          </Typography>
        ) : (
          <TextField
            label={t('offers.recipientPhone')}
            type="tel"
            value={phone}
            onChange={(event) => {
              setPhone(event.target.value);
            }}
            size="small"
            fullWidth
            required
          />
        )}

        <TextField
          label={t('offers.message')}
          helperText={t('offers.messageHint')}
          value={message}
          onChange={(event) => {
            setMessage(event.target.value);
          }}
          multiline
          minRows={3}
          size="small"
          fullWidth
          slotProps={{ htmlInput: { maxLength: 2000 } }}
        />

        <TextField
          label={t('offers.expiresAtField')}
          type="datetime-local"
          value={expiresAt}
          onChange={(event) => {
            setExpiresAt(event.target.value);
          }}
          size="small"
          fullWidth
          slotProps={{ inputLabel: { shrink: true } }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={deliver.isPending}>
          {tc('cancel')}
        </Button>
        <Button
          variant="contained"
          onClick={() => {
            void handleConfirm();
          }}
          disabled={!canSend}
          startIcon={deliver.isPending ? <CircularProgress size={16} /> : undefined}
        >
          {resend ? t('offers.resend') : t('offers.send')}
        </Button>
      </DialogActions>
      <ConfirmDialog
        open={confirmRecordOpen && orderer !== null}
        title={t('smsRecipients.confirmTitle')}
        message={orderer ? t('smsRecipients.confirmMessage', { name: orderer.name, number: orderer.smsDestinationMasked ?? '' }) : ''}
        confirmText={t('offers.noWhatsapp.record')}
        loading={recordNoWhatsApp.isPending}
        onCancel={() => {
          setConfirmRecordOpen(false);
        }}
        onConfirm={() => {
          setConfirmRecordOpen(false);
          void handleRecipientAction('record');
        }}
      />
    </ResponsiveDialog>
  );
}
